import { FulfillmentUnitStatus, MerchandisePurchaseItem } from "types";
import type { ReturnAdjustmentsByTemplateId } from "./ReturnInventory/returnVariantHelper";
import type { VariantQuantity } from "./updateQuantityHelper";
import { AdjustAction } from "@/components/Product/MerchandiseInventory/adjustInventoryActions";

interface FulfillmentUnitOperationPathData {
    unit: { id: ID };
    quantity: number;
    returnReason?: string;
    _damaged?: boolean;
}

export enum FulfillmentUnitOperationType {
    ADD = "addFulfillmentUnit",
    REMOVE = "removeFulfillmentUnit",
    RETURN = "returnFulfillmentUnit",
}

export interface FulfillmentUnitOperation {
    id: ID;
    path: string;
    op: FulfillmentUnitOperationType;
    data: FulfillmentUnitOperationPathData;
}

export interface UpdateQuantityPayload {
    items?: unknown[];
    errorStrategy?: "stop_on_failure" | "continue_on_failure";
    ops?: FulfillmentUnitOperation[];
    _priceStrategy?: "retain" | "latest";
    _partialRefund?: number;
    _emailOverrides?: {
        sendEmailToTraveler: boolean;
    };
}

const DEFAULT_RETURN_REASON = "Return/Restock";
const RETURN_OP_ID_PREFIX = `${FulfillmentUnitOperationType.RETURN}-`;
const REMOVE_OP_ID_PREFIX = `${FulfillmentUnitOperationType.REMOVE}-`;

const makeOpIdGenerator = (base: string) => {
    let index = 0;
    return () => `${base}-${index++}`;
};

type FulfillmentUnits = NonNullable<MerchandisePurchaseItem["fulfillmentUnits"]>;

interface TemplateUnitBuckets {
    fulfilled: FulfillmentUnits;
    unfulfilled: FulfillmentUnits;
}

interface PushOpInput {
    itemId: ID;
    ops: FulfillmentUnitOperation[];
    opId: ID;
    op: FulfillmentUnitOperationType;
    unitId: ID;
    quantity: number;
    returnReason?: string;
    isDamaged?: boolean;
}

interface PushOpsFromUnitsInput {
    itemId: ID;
    ops: FulfillmentUnitOperation[];
    getOpId: () => ID;
    op: FulfillmentUnitOperationType;
    units: FulfillmentUnits;
    returnReason?: string;
    isDamaged?: boolean;
}

const getUnitQuantityByUnitId = (units: FulfillmentUnits): Map<ID, number> => {
    const quantityByUnitId = new Map<ID, number>();

    for (const unit of units) {
        const unitId = unit.unit?.id;
        if (!unitId) {
            continue;
        }

        quantityByUnitId.set(unitId, (quantityByUnitId.get(unitId) ?? 0) + 1);
    }

    return quantityByUnitId;
};

const getFulfillmentUnitsByTemplateId = (item: MerchandisePurchaseItem): Record<string, TemplateUnitBuckets> => {
    const fulfillmentUnitsByTemplateId: Record<string, TemplateUnitBuckets> = {};

    for (const unit of item.fulfillmentUnits ?? []) {
        const templateId = unit.template?.id;
        if (!templateId) {
            continue;
        }

        if (!fulfillmentUnitsByTemplateId[templateId]) {
            fulfillmentUnitsByTemplateId[templateId] = { fulfilled: [], unfulfilled: [] };
        }

        if (unit.status === FulfillmentUnitStatus.FULFILLED) {
            fulfillmentUnitsByTemplateId[templateId].fulfilled.push(unit);
            continue;
        }

        fulfillmentUnitsByTemplateId[templateId].unfulfilled.push(unit);
    }

    return fulfillmentUnitsByTemplateId;
};

// Mutates units in-place via splice. Callers must pass disposable arrays.
const takeUnits = (units: FulfillmentUnits, qty: number): FulfillmentUnits => {
    if (qty <= 0) {
        return [];
    }

    return units.splice(0, Math.min(qty, units.length));
};

const pushOp = ({ itemId, ops, opId, op, unitId, quantity, returnReason, isDamaged }: PushOpInput): void => {
    if (quantity <= 0) {
        return;
    }

    ops.push({
        id: opId,
        path: `/items/${itemId}/fulfillmentUnits`,
        op,
        data: {
            unit: { id: unitId },
            quantity,
            returnReason,
            _damaged: isDamaged,
        },
    });
};

const pushOpsFromUnits = ({
    itemId,
    ops,
    getOpId,
    op,
    units,
    returnReason,
    isDamaged,
}: PushOpsFromUnitsInput): void => {
    for (const [unitId, quantity] of getUnitQuantityByUnitId(units).entries()) {
        pushOp({
            itemId,
            ops,
            opId: getOpId(),
            op,
            unitId,
            quantity,
            returnReason,
            isDamaged,
        });
    }
};

export const getFulfillmentUnitOps = (
    variantQuantities: VariantQuantity[],
    item: MerchandisePurchaseItem,
    returnAdjustmentsByTemplateId: ReturnAdjustmentsByTemplateId,
): FulfillmentUnitOperation[] => {
    const templateUnitsByTemplateId = getFulfillmentUnitsByTemplateId(item);
    const mutableUnitsByTemplateId: Record<string, TemplateUnitBuckets> = {};

    for (const [templateId, units] of Object.entries(templateUnitsByTemplateId)) {
        mutableUnitsByTemplateId[templateId] = {
            fulfilled: [...units.fulfilled],
            unfulfilled: [...units.unfulfilled],
        };
    }

    const ops: FulfillmentUnitOperation[] = [];
    for (const variant of variantQuantities) {
        const quantityDiff = variant.newQuantity - variant.currentQuantity;
        if (quantityDiff === 0) {
            continue;
        }

        const templateId = variant.templateId;
        const quantityOpId = templateId;
        const removeOpIdBase = `${REMOVE_OP_ID_PREFIX}${templateId}`;
        const returnOpIdBase = `${RETURN_OP_ID_PREFIX}${templateId}`;
        const getRemoveOpId = makeOpIdGenerator(removeOpIdBase);
        const getReturnOpId = makeOpIdGenerator(returnOpIdBase);

        if (quantityDiff > 0) {
            if (!variant.unitId) {
                continue;
            }

            pushOp({
                itemId: item.id,
                ops,
                opId: quantityOpId,
                op: FulfillmentUnitOperationType.ADD,
                unitId: variant.unitId,
                quantity: quantityDiff,
            });
            continue;
        }

        const templateUnitBuckets =
            mutableUnitsByTemplateId[templateId] ??
            (mutableUnitsByTemplateId[templateId] = { fulfilled: [], unfulfilled: [] });
        const reductionQuantity = Math.abs(quantityDiff);
        const maxUnfulfilledReduction = Math.max(0, variant.currentQuantity - variant.fulfilledQuantity);
        const removeTargetQty = Math.min(reductionQuantity, maxUnfulfilledReduction);
        const templateAdjustmentRows = returnAdjustmentsByTemplateId[templateId] ?? [];

        if (templateAdjustmentRows.length === 0) {
            const removedUnfulfilledUnits = takeUnits(templateUnitBuckets.unfulfilled, removeTargetQty);
            const remainingRemovalQty = reductionQuantity - removeTargetQty;

            pushOpsFromUnits({
                itemId: item.id,
                ops,
                getOpId: getRemoveOpId,
                op: FulfillmentUnitOperationType.REMOVE,
                units: removedUnfulfilledUnits,
            });

            const returnedFulfilledUnits = takeUnits(templateUnitBuckets.fulfilled, remainingRemovalQty);
            pushOpsFromUnits({
                itemId: item.id,
                ops,
                getOpId: getReturnOpId,
                op: FulfillmentUnitOperationType.RETURN,
                units: returnedFulfilledUnits,
                returnReason: DEFAULT_RETURN_REASON,
            });

            continue;
        }

        let remainingReductionQty = reductionQuantity;

        for (const row of templateAdjustmentRows) {
            if (remainingReductionQty <= 0) {
                break;
            }

            const returnQuantity = Math.min(Math.max(0, row.qty), remainingReductionQty);
            const returnedUnits = takeUnits(templateUnitBuckets.fulfilled, returnQuantity);
            pushOpsFromUnits({
                itemId: item.id,
                ops,
                getOpId: getReturnOpId,
                op: FulfillmentUnitOperationType.RETURN,
                units: returnedUnits,
                returnReason: row.reason || DEFAULT_RETURN_REASON,
                isDamaged: row.action === AdjustAction.MOVE_TO_UNAVAILABLE,
            });

            remainingReductionQty -= returnedUnits.length;
        }

        const fulfilledReductionQty = Math.max(0, reductionQuantity - removeTargetQty);
        const consumedFulfilledQty = reductionQuantity - remainingReductionQty;
        const remainingFulfilledQty = Math.max(0, fulfilledReductionQty - consumedFulfilledQty);
        const fallbackRemovalQty = Math.min(removeTargetQty, remainingReductionQty);

        const removedUnfulfilledUnits = takeUnits(templateUnitBuckets.unfulfilled, fallbackRemovalQty);
        pushOpsFromUnits({
            itemId: item.id,
            ops,
            getOpId: getRemoveOpId,
            op: FulfillmentUnitOperationType.REMOVE,
            units: removedUnfulfilledUnits,
        });

        remainingReductionQty -= removedUnfulfilledUnits.length;

        const fallbackReturnedQty = Math.max(remainingFulfilledQty, remainingReductionQty);
        const fallbackReturnedUnits = takeUnits(templateUnitBuckets.fulfilled, fallbackReturnedQty);
        pushOpsFromUnits({
            itemId: item.id,
            ops,
            getOpId: getReturnOpId,
            op: FulfillmentUnitOperationType.RETURN,
            units: fallbackReturnedUnits,
            returnReason: DEFAULT_RETURN_REASON,
        });
    }

    return ops;
};

interface BuildUpdateQuantityPayloadInput {
    variantQuantities: VariantQuantity[];
    item: MerchandisePurchaseItem;
    returnAdjustmentsByTemplateId: ReturnAdjustmentsByTemplateId;
}

export const buildUpdateQuantityPayload = ({
    variantQuantities,
    item,
    returnAdjustmentsByTemplateId,
}: BuildUpdateQuantityPayloadInput): UpdateQuantityPayload => {
    const fulfillmentUnitOps = getFulfillmentUnitOps(variantQuantities, item, returnAdjustmentsByTemplateId);

    return {
        errorStrategy: fulfillmentUnitOps.length > 0 ? "continue_on_failure" : undefined,
        ops: fulfillmentUnitOps.length > 0 ? fulfillmentUnitOps : undefined,
    };
};
